import { Component, OnInit, inject } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { ClientInvoiceExportDateField } from '../../../api/models/client-invoice-export-date-field';
import { ClientInvoiceExportRequest } from '../../../api/models/client-invoice-export-request';
import { ClientInvoiceExportScope } from '../../../api/models/client-invoice-export-scope';

// MatDatepicker emits Moment objects under MomentDateAdapter. We do
// not import `moment` directly — instead we duck-type the .format and
// .isAfter methods so the widget compiles without adding a direct
// runtime import.
interface MomentLike {
  format(fmt: string): string;
  isAfter(other: MomentLike | Date | string, granularity?: string): boolean;
}

function isMomentLike(value: unknown): value is MomentLike {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { format?: unknown }).format === 'function' &&
    typeof (value as { isAfter?: unknown }).isAfter === 'function'
  );
}

export interface ClientExportsModalData {
  clientId: number;
  clientName: string;
}

interface ScopeOption {
  value: ClientInvoiceExportScope;
  label: string;
}

@Component({
  selector: 'app-client-exports-modal',
  templateUrl: './client-exports-modal.component.html',
  styleUrls: ['./client-exports-modal.component.scss'],
})
export class ClientExportsModalComponent implements OnInit {
  private readonly builder = inject(UntypedFormBuilder);
  public readonly dialogRef = inject(MatDialogRef<ClientExportsModalComponent, ClientInvoiceExportRequest | undefined>);
  public readonly data = inject<ClientExportsModalData>(MAT_DIALOG_DATA);

  public form: UntypedFormGroup;

  public readonly scopeOptions: ScopeOption[] = [
    { value: ClientInvoiceExportScope.OriginalInvoice, label: 'Original Invoices' },
    { value: ClientInvoiceExportScope.OriginalInvoiceAttachment, label: 'Invoice Attachments' },
    { value: ClientInvoiceExportScope.Invoice, label: 'Invoices' },
    { value: ClientInvoiceExportScope.FinalReport, label: 'Final Report' },
  ];

  public readonly dateFieldOptions: { value: ClientInvoiceExportDateField; label: string }[] = [
    { value: ClientInvoiceExportDateField.Created, label: 'Created' },
    { value: ClientInvoiceExportDateField.DateFinanced, label: 'Financed' },
  ];

  public ngOnInit(): void {
    this.form = this.builder.group(
      {
        scope_original_invoice: [true],
        scope_original_invoice_attachment: [true],
        scope_invoice: [false],
        scope_final_report: [false],
        date_field: [ClientInvoiceExportDateField.Created, Validators.required],
        from: [null, Validators.required],
        to: [null, Validators.required],
      },
      { validators: [scopeMinOneValidator, dateRangeValidator] },
    );
  }

  public scopeControlName(value: ClientInvoiceExportScope): string {
    switch (value) {
      case ClientInvoiceExportScope.OriginalInvoice:
        return 'scope_original_invoice';
      case ClientInvoiceExportScope.OriginalInvoiceAttachment:
        return 'scope_original_invoice_attachment';
      case ClientInvoiceExportScope.Invoice:
        return 'scope_invoice';
      case ClientInvoiceExportScope.FinalReport:
        return 'scope_final_report';
    }
  }

  public submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const value = this.form.value;
    const scopes: ClientInvoiceExportScope[] = [];
    if (value.scope_original_invoice) {
      scopes.push(ClientInvoiceExportScope.OriginalInvoice);
    }
    if (value.scope_original_invoice_attachment) {
      scopes.push(ClientInvoiceExportScope.OriginalInvoiceAttachment);
    }
    if (value.scope_invoice) {
      scopes.push(ClientInvoiceExportScope.Invoice);
    }
    if (value.scope_final_report) {
      scopes.push(ClientInvoiceExportScope.FinalReport);
    }
    const request: ClientInvoiceExportRequest = {
      scopes,
      date_field: value.date_field,
      from: this.formatDate(value.from),
      to: this.formatDate(value.to),
    };
    this.dialogRef.close(request);
  }

  public cancel(): void {
    this.dialogRef.close();
  }

  // Moment objects come out of MatDatepicker under MomentDateAdapter;
  // tests may pass Date or string directly.
  private formatDate(value: unknown): string {
    if (!value) {
      return '';
    }
    if (isMomentLike(value)) {
      return value.format('YYYY-MM-DD');
    }
    if (value instanceof Date) {
      const y = value.getUTCFullYear().toString().padStart(4, '0');
      const m = (value.getUTCMonth() + 1).toString().padStart(2, '0');
      const d = value.getUTCDate().toString().padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
    return String(value);
  }
}

const scopeMinOneValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
  const a = group.get('scope_original_invoice')?.value;
  const b = group.get('scope_original_invoice_attachment')?.value;
  const c = group.get('scope_invoice')?.value;
  const d = group.get('scope_final_report')?.value;
  return a || b || c || d ? null : { scopeRequired: true };
};

const dateRangeValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
  const from = group.get('from')?.value;
  const to = group.get('to')?.value;
  if (!from || !to) {
    return null;
  }
  if (isMomentLike(from) && isMomentLike(to)) {
    return from.isAfter(to, 'day') ? { dateRange: true } : null;
  }
  const fromDate = from instanceof Date ? from : new Date(String(from));
  const toDate = to instanceof Date ? to : new Date(String(to));
  if (Number.isNaN(fromDate.getTime()) || Number.isNaN(toDate.getTime())) {
    return null;
  }
  return fromDate.getTime() > toDate.getTime() ? { dateRange: true } : null;
};
